import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import firebase from "firebase/app";
import "firebase/firestore";
import ReCAPTCHA from "react-google-recaptcha";
import logo from "../assets/logo.png";
import cafeOrderingImg from "../assets/atTheBar.png";
import outdoorServiceImg from "../assets/wheelchairStairs.png";
import parkingAccessibilityImg from "../assets/disabledParking.png";
import highTablesImg from "../assets/venue accessible.svg";
import cafeFriendsImg from "../assets/handPhone.png";
import carersOutingImg from "../assets/accessibility requirement.svg";
import browseOrderImg from "../assets/9.png";
import cafeAccessibleImg from "../assets/cafe accessible.svg";
import checkFirstImg from "../assets/Artwork-1781.png";
import googlePlayButton from "../assets/Google Play App Button.png";
import appStoreButton from "../assets/Apple App Store Button.png";
import serveEveryoneIcon from "../assets/Serve disabled customers.svg";
import knowBeforeIcon from "../assets/Know before they arrive.svg";
import cannotAccommodateIcon from "../assets/CannotAccomodate.svg";
import blindAccessIcon from "../assets/VisionImpaired.svg";
import browseNearbyIcon from "../assets/browse nearby.svg";
import doItYourselfIcon from "../assets/do-it-yourself ordering systems.svg";
import SEO from "../components/SEO";
import FooterPatentList from "../components/FooterPatentList";
import useScrollToHash from "../hooks/useScrollToHash";

export const LandingV2 = () => {
  const history = useHistory();
  // Make direct links like piatto.com.au/#v2-get-app land on the section.
  useScrollToHash();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const [backToTopVisible, setBackToTopVisible] = useState(false);
  const [textSizeIndex, setTextSizeIndex] = useState(0);
  const [isReading, setIsReading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [a11yAnnouncement, setA11yAnnouncement] = useState('');
  const [formFeedback, setFormFeedback] = useState({ message: '', type: '' });
  const [recaptchaValue, setRecaptchaValue] = useState(false);

  const heroRef = useRef(null);
  const captchaRef = useRef(null);
  const mainContentRef = useRef(null);
  const menuButtonRef = useRef(null);
  const readIndexRef = useRef(0);
  const isReadingRef = useRef(false);

  const textSizes = ['default', 'large', 'larger', 'largest'];
  const prefersReducedMotion = typeof window !== 'undefined'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

  const announceToScreenReader = (message) => {
    setA11yAnnouncement('');
    setTimeout(() => setA11yAnnouncement(message), 50);
  };

  // Scroll Progress Bar
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;
      const progressBar = document.getElementById('v2-scroll-progress');
      if (progressBar) {
        progressBar.style.width = scrollPercent + '%';
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Header Scroll & Back to Top
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setHeaderScrolled(!entry.isIntersecting);
          setBackToTopVisible(!entry.isIntersecting);
        });
      },
      { threshold: 0, rootMargin: '-100px 0px 0px 0px' }
    );

    if (heroRef.current) {
      observer.observe(heroRef.current);
    }

    return () => {
      if (heroRef.current) {
        observer.unobserve(heroRef.current);
      }
    };
  }, []);

  // Parallax Effect on feature images
  useEffect(() => {
    if (prefersReducedMotion) return;

    const handleParallax = () => {
      const parallaxElements = document.querySelectorAll('.v2-feature-image');
      parallaxElements.forEach(el => {
        const speed = 0.04;
        const rect = el.getBoundingClientRect();
        const centerY = rect.top + rect.height / 2;
        const viewportCenter = window.innerHeight / 2;
        const offset = (centerY - viewportCenter) * speed;
        el.style.transform = `translateY(${offset}px)`;
        el.style.willChange = 'transform';
      });
    };

    handleParallax();
    window.addEventListener('scroll', handleParallax, { passive: true });
    return () => window.removeEventListener('scroll', handleParallax);
  }, [prefersReducedMotion]);

  // Scroll Reveal Animations
  useEffect(() => {
    if (prefersReducedMotion) {
      document.querySelectorAll('.v2-reveal, .v2-reveal-left, .v2-reveal-right, .v2-reveal-scale').forEach(el => {
        el.classList.add('v2-revealed');
      });
      return;
    }

    const revealElements = document.querySelectorAll('.v2-reveal, .v2-reveal-left, .v2-reveal-right, .v2-reveal-scale');

    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, index) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.classList.add('v2-revealed');
            }, index * 50);
            revealObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    revealElements.forEach(el => revealObserver.observe(el));

    return () => revealObserver.disconnect();
  }, [prefersReducedMotion]);

  // Mobile Menu Toggle
  const toggleMobileMenu = () => {
    const opening = !mobileMenuOpen;
    setMobileMenuOpen(opening);
    if (opening) {
      setTimeout(() => {
        const firstLink = document.querySelector('#v2-mobile-nav a');
        if (firstLink) firstLink.focus();
      }, 100);
    }
  };

  // Mobile Menu Escape Key & Focus Trap
  useEffect(() => {
    if (!mobileMenuOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
        menuButtonRef.current?.focus();
        return;
      }

      if (e.key === 'Tab') {
        const mobileNav = document.getElementById('v2-mobile-nav');
        if (!mobileNav) return;
        const focusable = mobileNav.querySelectorAll('a, button');
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileMenuOpen]);

  // Back to Top
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
  };

  // Text Size Toggle
  const toggleTextSize = () => {
    const newIndex = (textSizeIndex + 1) % textSizes.length;
    const newSize = textSizes[newIndex];
    setTextSizeIndex(newIndex);

    const sizeMap = {
      'default': '20px',
      'large': '22px',
      'larger': '24px',
      'largest': '26px'
    };

    document.documentElement.style.fontSize = sizeMap[newSize];
    document.documentElement.style.setProperty('--font-size-base', sizeMap[newSize]);

    textSizes.forEach(size => {
      if (size !== 'default') {
        document.documentElement.classList.remove(`text-size-${size}`);
      }
    });
    if (newSize !== 'default') {
      document.documentElement.classList.add(`text-size-${newSize}`);
    }

    announceToScreenReader(`Text size changed to ${newSize}`);
    localStorage.setItem('piatto-text-size', newSize);
  };

  // Load saved text size
  useEffect(() => {
    const savedSize = localStorage.getItem('piatto-text-size');
    if (savedSize) {
      const index = textSizes.indexOf(savedSize);
      if (index !== -1) {
        setTextSizeIndex(index);

        const sizeMap = {
          'default': '20px',
          'large': '22px',
          'larger': '24px',
          'largest': '26px'
        };

        document.documentElement.style.fontSize = sizeMap[savedSize];
        document.documentElement.style.setProperty('--font-size-base', sizeMap[savedSize]);

        if (savedSize !== 'default') {
          document.documentElement.classList.add(`text-size-${savedSize}`);
        }
      }
    }
  }, [textSizes]);

  // Read Aloud
  const readNextElement = () => {
    if (!mainContentRef.current || !isReadingRef.current) {
      return;
    }

    const elements = mainContentRef.current.querySelectorAll('h1, h2, h3, p, li');

    if (readIndexRef.current >= elements.length) {
      isReadingRef.current = false;
      setIsReading(false);
      document.querySelectorAll('.v2-reading-highlight').forEach(el => el.classList.remove('v2-reading-highlight'));
      return;
    }

    const element = elements[readIndexRef.current];
    const text = element.textContent.trim();

    if (text) {
      document.querySelectorAll('.v2-reading-highlight').forEach(el => {
        el.classList.remove('v2-reading-highlight');
      });

      element.classList.add('v2-reading-highlight');
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.9;
      utterance.onend = () => {
        if (isReadingRef.current) {
          readIndexRef.current++;
          setTimeout(() => readNextElement(), 100);
        }
      };
      utterance.onerror = () => {
        if (isReadingRef.current) {
          readIndexRef.current++;
          readNextElement();
        }
      };
      window.speechSynthesis.speak(utterance);
    } else {
      readIndexRef.current++;
      readNextElement();
    }
  };

  const toggleReadAloud = () => {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      alert('Sorry, your browser does not support text-to-speech.');
      return;
    }

    if (isReadingRef.current) {
      window.speechSynthesis.cancel();
      if (window.speechSynthesis.speaking) {
        window.speechSynthesis.pause();
        window.speechSynthesis.cancel();
      }
      isReadingRef.current = false;
      setIsReading(false);
      announceToScreenReader('Reading stopped');
      document.querySelectorAll('.v2-reading-highlight').forEach(el => {
        el.classList.remove('v2-reading-highlight');
      });
    } else {
      isReadingRef.current = true;
      setIsReading(true);
      announceToScreenReader('Reading page content aloud');
      readIndexRef.current = 0;
      readNextElement();
    }
  };

  // Smooth Scroll for Anchor Links
  useEffect(() => {
    const handleAnchorClick = (e) => {
      const target = e.target.closest('a[href^="#"]');
      if (!target) return;

      const href = target.getAttribute('href');
      if (href === '#') return;

      e.preventDefault();
      const element = document.querySelector(href);
      if (element) {
        const headerOffset = 120;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: prefersReducedMotion ? 'auto' : 'smooth'
        });

        setMobileMenuOpen(false);

        element.setAttribute('tabindex', '-1');
        setTimeout(() => {
          element.focus();
          element.addEventListener('blur', () => element.removeAttribute('tabindex'), { once: true });
        }, 500);
      }
    };

    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, [prefersReducedMotion]);

  // Contact Form Handler
  const handleContactSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData(e.target);
      const contactData = {
        firstName: formData.get('v2-first-name'),
        lastName: formData.get('v2-last-name'),
        email: formData.get('v2-email'),
        phone: formData.get('v2-phone') || '',
        interest: formData.get('v2-interest'),
        message: formData.get('v2-message'),
        submittedAt: firebase.firestore.FieldValue.serverTimestamp(),
        status: 'new',
        source: 'landing-v2'
      };

      await firebase.firestore().collection('ContactRequest').add(contactData);

      e.target.reset();
      captchaRef.current.reset();
      setRecaptchaValue(false);

      setFormFeedback({ message: 'Message sent. Thank you for contacting us. We will be in touch soon.', type: 'success' });
      setTimeout(() => {
        const feedback = document.querySelector('.v2-form-feedback');
        if (feedback) { feedback.setAttribute('tabindex', '-1'); feedback.focus(); }
      }, 100);

    } catch (error) {
      setFormFeedback({ message: `Submission failed: ${error.message}. Please try again or email us directly.`, type: 'error' });
      setTimeout(() => {
        const feedback = document.querySelector('.v2-form-feedback');
        if (feedback) { feedback.setAttribute('tabindex', '-1'); feedback.focus(); }
      }, 100);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Accessibility for Hospitality"
        description="Piatto sends your guests' access needs to your venue before they arrive. Mobility, sensory, dietary — you are ready the moment they walk in. Free for venues. Available on iOS and Android."
        url="https://piatto.com.au/preview"
        image="https://piatto.com.au/logo512.png"
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Piatto",
            url: "https://piatto.com.au",
            logo: "https://piatto.com.au/logo512.png",
            description: "Accessibility for Hospitality. Your guests set their access needs once and Piatto sends them to you before they arrive.",
            sameAs: [
              "http://youtube.com/@piattoapp",
              "https://www.instagram.com/piattoit",
              "https://www.facebook.com/piattoAUS",
              "https://twitter.com/@PiattoNow",
              "https://piatto.substack.com/",
              "https://www.linkedin.com/company/piattoaus/",
              "https://discord.com/invite/6dcdt2d24x"
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "MobileApplication",
            name: "Piatto",
            operatingSystem: "iOS, Android",
            applicationCategory: "FoodAndDrinkApplication",
            offers: { "@type": "Offer", price: "0", priceCurrency: "AUD" },
          },
        ]}
      />

      {/* Scroll Progress Bar */}
      <div id="v2-scroll-progress" className="v2-scroll-progress" aria-hidden="true"></div>

      {/* Skip Link */}
      <a href="#v2-main-content" className="v2-skip-link">Skip to main content</a>

      {/* Screen Reader Announcements */}
      <div aria-live="assertive" aria-atomic="true" className="v2-visually-hidden" role="status">
        {a11yAnnouncement}
      </div>

      {/* Accessibility Toolbar */}
      <div className="v2-a11y-toolbar" role="group" aria-label="Accessibility tools">
        <button
          className={`v2-a11y-btn ${backToTopVisible ? 'v2-a11y-btn-visible' : 'v2-a11y-btn-hidden'}`}
          onClick={scrollToTop}
          aria-label="Back to top"
          title="Back to top"
        >
          <span className="v2-a11y-btn-tooltip">Back to Top</span>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="18 15 12 9 6 15"/>
          </svg>
        </button>
        <button
          className="v2-a11y-btn"
          onClick={toggleTextSize}
          aria-label={`Text size: ${textSizes[textSizeIndex]}. Click to change.`}
          title="Increase text size"
        >
          <span className="v2-a11y-btn-tooltip">Text Size</span>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="4 7 4 4 20 4 20 7"/>
            <line x1="9" y1="20" x2="15" y2="20"/>
            <line x1="12" y1="4" x2="12" y2="20"/>
          </svg>
        </button>
        <button
          className="v2-a11y-btn"
          onClick={toggleReadAloud}
          aria-label={isReading ? 'Stop reading' : 'Read page aloud'}
          aria-pressed={isReading}
          title="Read page aloud"
        >
          <span className="v2-a11y-btn-tooltip">Read Aloud</span>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
            <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
          </svg>
        </button>
      </div>

      {/* Header / Navigation */}
      <header className={`v2-header ${headerScrolled ? 'v2-scrolled' : ''}`} id="v2-header" role="banner">
        <div className="v2-header-inner">
          <a href="/" className="v2-logo" aria-label="Piatto - Home">
            <img src={logo} alt="Piatto" className="v2-logo-image" />
          </a>

          <nav className="v2-nav-center" role="navigation" aria-label="Main navigation">
            <div className="v2-nav-center-links">
              <a href="#v2-venues" className="v2-nav-link">For Venues</a>
              <a href="#v2-carers" className="v2-nav-link">For Carers</a>
              <a href="#v2-get-app" className="v2-nav-link">Get the App</a>
              <a href="/SignIn" className="v2-nav-link">Venue Log-In</a>
              <a href="#v2-contact" className="v2-nav-link">Contact</a>
            </div>
          </nav>

          <a href="/SignUp" className={`v2-btn v2-btn-primary v2-header-cta ${headerScrolled ? 'v2-header-cta-visible' : ''}`}>
            Venue Sign-Up <span className="v2-free-badge">Free</span>
          </a>

          <a href="/SignUp" className={`v2-btn v2-btn-primary v2-header-cta-mobile ${headerScrolled ? 'v2-header-cta-visible' : ''}`}>
            Sign-Up <span className="v2-free-badge">Free</span>
          </a>

          <button
            className="v2-mobile-menu-btn"
            ref={menuButtonRef}
            aria-expanded={mobileMenuOpen}
            aria-controls="v2-mobile-nav"
            aria-label="Toggle menu"
            onClick={toggleMobileMenu}
          >
            <span></span><span></span><span></span>
          </button>
        </div>

        <nav id="v2-mobile-nav" className={`v2-mobile-nav ${mobileMenuOpen ? 'v2-open' : ''}`} aria-label="Mobile navigation" aria-hidden={!mobileMenuOpen}>
          <a href="#v2-venues" onClick={() => { setMobileMenuOpen(false); menuButtonRef.current?.focus(); }}>For Venues</a>
          <a href="#v2-carers" onClick={() => { setMobileMenuOpen(false); menuButtonRef.current?.focus(); }}>For Carers</a>
          <a href="#v2-get-app" onClick={() => { setMobileMenuOpen(false); menuButtonRef.current?.focus(); }}>Get the App</a>
          <a href="/SignIn" onClick={() => { setMobileMenuOpen(false); menuButtonRef.current?.focus(); }}>Venue Log-In</a>
          <a href="#v2-contact" onClick={() => { setMobileMenuOpen(false); menuButtonRef.current?.focus(); }}>Contact</a>
        </nav>
      </header>

      <main id="v2-main-content" ref={mainContentRef}>

        {/* ===== 4.2 Hero Section ===== */}
        <section className="v2-hero" id="v2-hero" ref={heroRef} aria-labelledby="v2-hero-title">
          <div className="v2-hero-gradient" aria-hidden="true"></div>

          <div className="v2-hero-inner">
            <div className="v2-hero-grid">
              <div className="v2-hero-content v2-reveal-left">
                <h1 id="v2-hero-title" className="v2-hero-title">Accessibility for Hospitality</h1>
                <p className="v2-hero-subtitle">
                  Your guests set their access needs once — mobility, sensory, dietary — and Piatto sends them to you before they arrive. You're ready the moment they walk in, with no guesswork at the counter. Every guest welcomed, every order understood.
                </p>
                <div className="v2-hero-cta">
                  <a href="/SignUp" className="v2-btn v2-btn-primary v2-btn-large">
                    Venue Sign-Up <span className="v2-free-badge">Free</span>
                  </a>
                </div>
                <p className="v2-hero-app-note">
                  Looking for the app? Get it on the{' '}
                  <a href="https://apps.apple.com/au/app/piatto-order-ahead-for-all/id1602642481" className="v2-hero-link" target="_blank" rel="noopener noreferrer">App Store</a>
                  {' / '}
                  <a href="https://play.google.com/store/apps/details?id=com.cubage.winndine&hl=en_AU" className="v2-hero-link" target="_blank" rel="noopener noreferrer">Google Play</a>.
                </p>
              </div>
              <div className="v2-hero-visual v2-reveal-right">
                <img
                  src={cafeAccessibleImg}
                  alt="A waiter welcomes a young person using a wheelchair to a table inside an accessible venue, with a companion walking behind"
                  className="v2-hero-image"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ===== 4.3 Impact Band ===== */}
        <section className="v2-impact-band v2-reveal" aria-labelledby="v2-impact-title">
          <p id="v2-impact-title" className="v2-impact-text">
            When the building can't be made accessible, the service still can.
          </p>
        </section>

        {/* ===== 4.4 How It Works ===== */}
        <section className="v2-how-it-works" aria-labelledby="v2-how-it-works-title">
          <div className="v2-section-inner">
            <header className="v2-section-header v2-reveal">
              <h2 id="v2-how-it-works-title" className="v2-section-title">How It Works</h2>
              <p className="v2-section-description">Simple for venues. A relief for customers.</p>
            </header>
            <div className="v2-how-grid">
              <div className="v2-how-card v2-reveal-left">
                <h3>For Venues <span className="v2-free-badge-alt">Free to join</span></h3>
                <ol className="v2-how-steps">
                  <li><span><strong>Sign up</strong> in about two minutes.</span></li>
                  <li><span><strong>Add your menu</strong> and venue details.</span></li>
                  <li><span><strong>Receive orders</strong> — with each customer's needs already attached.</span></li>
                </ol>
              </div>
              <div className="v2-how-card v2-reveal-right">
                <h3>For Customers <span className="v2-free-badge-alt">Free app</span></h3>
                <ol className="v2-how-steps">
                  <li><span><strong>Download</strong> the Piatto app.</span></li>
                  <li><span><strong>Browse</strong> any venue's menu and check its accessibility.</span></li>
                  <li><span><strong>Order ahead</strong> — no queueing, no explaining, no barriers.</span></li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        {/* ===== 4.5 Why Venues Join ===== */}
        <section className="v2-section v2-section-light" id="v2-venues" aria-labelledby="v2-venues-title">
          <div className="v2-section-inner">
            <header className="v2-section-header v2-reveal">
              <h2 id="v2-venues-title" className="v2-section-title">Why venues join</h2>
            </header>
            <div className="v2-venue-cards-grid">
              <div className="v2-venue-card v2-reveal">
                <div className="v2-venue-card-icon" aria-hidden="true">
                  <img src={serveEveryoneIcon} alt="" />
                </div>
                <h3>Serve faster, waste less.</h3>
                <p>Orders arrive with access and dietary needs already attached — no back-and-forth at the counter or the table. Staff serve on the first pass: quicker turns, fewer remakes.</p>
              </div>
              <div className="v2-venue-card v2-reveal">
                <div className="v2-venue-card-icon" aria-hidden="true">
                  <img src={browseNearbyIcon} alt="" />
                </div>
                <h3>Reach guests other venues miss.</h3>
                <p>A whole segment of customers currently plans their day around which places are worth the effort. Make it easy, and they choose you — and come back.</p>
              </div>
              <div className="v2-venue-card v2-reveal">
                <div className="v2-venue-card-icon" aria-hidden="true">
                  <img src={cannotAccommodateIcon} alt="" />
                </div>
                <h3>Serve everyone, even if the building isn't perfect.</h3>
                <p>Stairs, high tables, a food-truck setup — Piatto lets you take the order and bring it out. No QR gymnastics required.</p>
              </div>
              <div className="v2-venue-card v2-reveal">
                <div className="v2-venue-card-icon" aria-hidden="true">
                  <img src={doItYourselfIcon} alt="" />
                </div>
                <h3>Show what you offer.</h3>
                <p>Ramp, accessible bathroom, braille or audio-narrated menu, a quiet corner for sensory needs — list it, and the customers who need it can find it.</p>
              </div>
              <div className="v2-venue-card v2-reveal">
                <div className="v2-venue-card-icon" aria-hidden="true">
                  <img src={blindAccessIcon} alt="" />
                </div>
                <h3>Accessible menus, built in.</h3>
                <p>Most online menus can't be read by a screen reader. On Piatto, yours can — so blind and low-vision customers order independently.</p>
              </div>
              <div className="v2-venue-card v2-reveal">
                <div className="v2-venue-card-icon" aria-hidden="true">
                  <img src={knowBeforeIcon} alt="" />
                </div>
                <h3>Know before they arrive.</h3>
                <p>Each order tells you what a customer needs, so you're ready to help the moment they reach you.</p>
              </div>
            </div>
            <div className="v2-venue-cta v2-reveal">
              <a href="/SignUp" className="v2-btn v2-btn-primary v2-btn-large">
                Venue Sign-Up <span className="v2-free-badge">Free</span>
              </a>
            </div>
          </div>
        </section>

        {/* ===== 4.6 The Problem ===== */}
        <section className="v2-section v2-section-white" aria-labelledby="v2-problem-title">
          <div className="v2-section-inner">
            <header className="v2-section-header v2-reveal">
              <h2 id="v2-problem-title" className="v2-section-title">The problem we're solving</h2>
            </header>
            <div className="v2-problem-intro v2-reveal">
              <p>
                Today, a customer with an access need runs the same loop at every venue: explain, hope, repeat. Flag down staff. Ask other diners for help. Sometimes give up and stay home.
              </p>
              <p>
                Piatto ends the loop. Needs are set once and travel with the order, so people are understood before they arrive — through the same system that handles every other order.
              </p>
            </div>
            <div className="v2-problem-grid">
              <div className="v2-problem-points v2-reveal-left">
                <div className="v2-problem-point">
                  <h3>Order independently.</h3>
                  <p>No flagging down staff, no asking other diners for help.</p>
                </div>
                <div className="v2-problem-point">
                  <h3>Every setup included.</h3>
                  <p>Food trucks, markets and pop-ups can serve every customer, whatever the physical layout.</p>
                </div>
                <div className="v2-problem-point">
                  <h3>The service can be accessible even when the building isn't.</h3>
                  <p>Stairs or high tables don't have to mean "sorry, we can't."</p>
                </div>
                <div className="v2-problem-tagline v2-reveal">
                  <p>Enabling all hospitality, for all people.</p>
                </div>
              </div>
              <div className="v2-problem-visuals v2-reveal-right">
                <img
                  src={highTablesImg}
                  alt="Illustration of a waiter welcoming a wheelchair user to an accessible table in a venue"
                  className="v2-feature-image"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ===== 4.7 Designed for People ===== */}
        <section className="v2-section v2-section-cream" aria-labelledby="v2-designed-title">
          <div className="v2-section-inner">
            <div className="v2-feature-block">
              <div className="v2-feature-content v2-reveal-left">
                <h2 id="v2-designed-title">Designed for people, not just phone numbers</h2>
                <p>
                  Scan the QR. Open the web app. Type your number. Wait for the code. Enter the code. How front-of-mind do you think accessibility was when those systems were designed?
                </p>
                <p>
                  Piatto starts from the opposite end. Order without speaking a word — your order and your needs go straight to the kitchen. For anyone who finds talking, hearing, or reading a menu at the counter hard, that's the difference between ordering and giving up.
                </p>
              </div>
              <div className="v2-feature-visual v2-reveal-right">
                <img
                  src={cafeOrderingImg}
                  alt="Person ordering coffee at a cafe counter"
                  className="v2-feature-image"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ===== 4.8 How Piatto Helps You ===== */}
        <section className="v2-section v2-section-white" aria-labelledby="v2-helps-title">
          <div className="v2-section-inner">
            <header className="v2-section-header v2-reveal">
              <h2 id="v2-helps-title" className="v2-section-title">How Piatto helps you</h2>
            </header>

            <article className="v2-feature-block">
              <div className="v2-feature-content v2-reveal-left">
                <h3>Set your needs once.</h3>
                <p>
                  Save your access requirements — and your car rego for curbside, if you like — to your profile. When you order, they go to the venue automatically. No explaining at the door, ever again.
                </p>
              </div>
              <div className="v2-feature-visual v2-reveal-right">
                <img
                  src={parkingAccessibilityImg}
                  alt="A birds eye view of three parking spaces with a sports car in the first one and two empty spaces marked with the universal disabled symbol"
                  className="v2-feature-image"
                />
              </div>
            </article>

            <article className="v2-feature-block v2-reverse">
              <div className="v2-feature-content v2-reveal-right">
                <h3>Check before you make the trip.</h3>
                <p>
                  See the menu, the accessibility details, and what's actually available right now — is breakfast still on, is the coffee machine running — before you spend the effort getting there. If getting to the counter is hard, order on approach and the venue brings it to you, because they knew you were coming.
                </p>
              </div>
              <div className="v2-feature-visual v2-reveal-left">
                <img
                  src={checkFirstImg}
                  alt="A 3D rendering of a ramp and flat surface with tactile paving, a girl with a cane following the paving and a boy in a wheelchair at the bottom of the ramp"
                  className="v2-feature-image"
                />
              </div>
            </article>

            <article className="v2-feature-block">
              <div className="v2-feature-content v2-reveal-left">
                <h3>Be met with service, not a scramble.</h3>
                <p>
                  The venue gets a heads-up that you're on the way and how to help — so you're looked after from the moment you arrive. No shouting across the room, no waiting to be noticed.
                </p>
              </div>
              <div className="v2-feature-visual v2-reveal-right">
                <img
                  src={browseOrderImg}
                  alt="Person relaxing in a chair while browsing a menu on their phone and making selections before leaving"
                  className="v2-feature-image"
                />
              </div>
            </article>
          </div>
        </section>

        {/* ===== 4.9 Pink Callout ===== */}
        <section className="v2-callout-band v2-reveal" aria-labelledby="v2-callout-text">
          <p id="v2-callout-text" className="v2-callout-text">
            Piatto is built on accessibility, inclusion, and ease of use. Tell us how we can do it better.
          </p>
        </section>

        {/* ===== 4.10 Carers & Support Workers ===== */}
        <section className="v2-section v2-carers-section" id="v2-carers" aria-labelledby="v2-carers-title">
          <div className="v2-section-inner">
            <div className="v2-carers-grid">
              <div className="v2-carers-content v2-reveal-left">
                <h2 id="v2-carers-title">For carers and support workers</h2>
                <p>Personal and executive assistants, support workers, carers and family members spend hours researching venues. Piatto makes it simple.</p>
                <ul className="v2-carers-list">
                  <li>Check a venue's accessibility details before you leave</li>
                  <li>Your client's requirements are shared automatically when ordering</li>
                  <li>No more phone calls or surprises on arrival</li>
                  <li>Order ahead so you can focus on the outing</li>
                  <li>Leave accessibility reviews to help other carers</li>
                  <li>Plan the outing but direct the bill elsewhere</li>
                </ul>
                <div className="v2-carers-quote">
                  <p className="v2-carers-quote-label"><em>How it could sound:</em></p>
                  <p><em>"I used to spend hours calling venues and hoping for the best. Now I check Piatto and we're out the door."</em></p>
                </div>
              </div>
              <div className="v2-carers-visual v2-reveal-right">
                <img
                  src={carersOutingImg}
                  alt="Illustration representing accessibility requirements and planning for carers and support workers"
                  className="v2-feature-image"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ===== 4.11 Accessibility Grid ===== */}
        <section className="v2-section v2-section-light" aria-labelledby="v2-a11y-grid-title">
          <div className="v2-section-inner">
            <header className="v2-section-header v2-reveal">
              <h2 id="v2-a11y-grid-title" className="v2-section-title">Accessibility for everyone</h2>
            </header>
            <div className="v2-a11y-grid">
              <div className="v2-a11y-card v2-reveal">
                <div className="v2-a11y-card-icon" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                </div>
                <h3>Vision impaired</h3>
                <p>Menus that work with screen readers, so you can read and order independently.</p>
              </div>
              <div className="v2-a11y-card v2-reveal">
                <div className="v2-a11y-card-icon" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                    <line x1="12" y1="19" x2="12" y2="23"/>
                    <line x1="8" y1="23" x2="16" y2="23"/>
                  </svg>
                </div>
                <h3>Hearing impaired</h3>
                <p>Order without speaking or listening for a called name; updates come to your phone.</p>
              </div>
              <div className="v2-a11y-card v2-reveal">
                <div className="v2-a11y-card-icon" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 16v-4"/>
                    <path d="M12 8h.01"/>
                  </svg>
                </div>
                <h3>Cognitive</h3>
                <p>A clear, simple interface with dyslexia-friendly options. Browse at your own pace, no pressure.</p>
              </div>
              <div className="v2-a11y-card v2-reveal">
                <div className="v2-a11y-card-icon" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <circle cx="12" cy="12" r="4"/>
                    <line x1="21.17" y1="8" x2="12" y2="8"/>
                    <line x1="3.95" y1="6.06" x2="8.54" y2="14"/>
                    <line x1="10.88" y1="21.94" x2="15.46" y2="14"/>
                  </svg>
                </div>
                <h3>Mobility impaired</h3>
                <p>Is there a ramp? How steep? Order without entering, and the venue knows you're coming.</p>
              </div>
              <div className="v2-a11y-card v2-reveal">
                <div className="v2-a11y-card-icon" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </div>
                <h3>Temporarily challenged</h3>
                <p>Pregnant, injured, pushing a pram, carrying luggage? Check the menu and the layout before you arrive — no wasted trips to find stairs or sold-out items.</p>
              </div>
              <div className="v2-a11y-card v2-reveal">
                <div className="v2-a11y-card-icon" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                </div>
                <h3>Live availability</h3>
                <p>Is the venue open, or closing soon? Still serving breakfast? Coffee machine on? Check before you make the effort.</p>
              </div>
            </div>
          </div>
        </section>

        {/* ===== 4.12 Get the App ===== */}
        <section className="v2-get-app-section" id="v2-get-app" aria-labelledby="v2-get-app-title">
          <div className="v2-get-app-inner">
            <h2 id="v2-get-app-title" className="v2-get-app-title v2-reveal"><a href="https://piatto.com.au/app" className="v2-get-app-title-link">Get the Piatto app</a></h2>
            <p className="v2-get-app-text v2-reveal">Set your needs once and let hospitality come to you.</p>
            <div className="v2-app-badges v2-reveal">
              <a
                href="https://apps.apple.com/au/app/piatto-order-ahead-for-all/id1602642481"
                className="v2-app-badge-link"
                aria-label="Download Piatto on the App Store"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={appStoreButton} alt="Download on the App Store" className="v2-app-badge-img" />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.cubage.winndine&hl=en_AU"
                className="v2-app-badge-link"
                aria-label="Get Piatto on Google Play"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={googlePlayButton} alt="Get it on Google Play" className="v2-app-badge-img" />
              </a>
            </div>
          </div>
        </section>

        {/* ===== 4.13 Contact Form ===== */}
        <section id="v2-contact" className="v2-contact-section" aria-labelledby="v2-contact-title">
          <div className="v2-contact-inner">
            <header className="v2-contact-header v2-reveal">
              <h2 id="v2-contact-title" className="v2-contact-title">Get in Touch</h2>
              <p className="v2-contact-subtitle">Interested in Piatto? Have questions? We would love to hear from you.</p>
            </header>
            <form className="v2-contact-form v2-reveal-scale" onSubmit={handleContactSubmit} aria-label="Contact form">
              <div className="v2-form-row v2-two-col">
                <div className="v2-form-group">
                  <label htmlFor="v2-first-name" className="v2-form-label">First Name <span className="v2-required" aria-hidden="true">*</span></label>
                  <input type="text" id="v2-first-name" name="v2-first-name" className="v2-form-input" required aria-required="true" autoComplete="given-name" placeholder="Your first name" />
                </div>
                <div className="v2-form-group">
                  <label htmlFor="v2-last-name" className="v2-form-label">Last Name <span className="v2-required" aria-hidden="true">*</span></label>
                  <input type="text" id="v2-last-name" name="v2-last-name" className="v2-form-input" required aria-required="true" autoComplete="family-name" placeholder="Your last name" />
                </div>
              </div>
              <div className="v2-form-row v2-two-col">
                <div className="v2-form-group">
                  <label htmlFor="v2-email" className="v2-form-label">Email <span className="v2-required" aria-hidden="true">*</span></label>
                  <input type="email" id="v2-email" name="v2-email" className="v2-form-input" required aria-required="true" autoComplete="email" placeholder="you@example.com" />
                </div>
                <div className="v2-form-group">
                  <label htmlFor="v2-phone" className="v2-form-label">Phone</label>
                  <input
                    type="tel"
                    id="v2-phone"
                    name="v2-phone"
                    className="v2-form-input"
                    autoComplete="tel"
                    placeholder="0400 000 000"
                    pattern="[0-9]*"
                    inputMode="numeric"
                    aria-describedby="v2-phone-hint"
                    onInput={(e) => e.target.value = e.target.value.replace(/[^0-9]/g, '')}
                  />
                  <span id="v2-phone-hint" className="v2-phone-hint">Numbers only (e.g. 0400000000)</span>
                </div>
              </div>
              <div className="v2-form-row">
                <div className="v2-form-group">
                  <label htmlFor="v2-interest" className="v2-form-label">I am interested as a... <span className="v2-required" aria-hidden="true">*</span></label>
                  <select id="v2-interest" name="v2-interest" className="v2-form-select" required aria-required="true">
                    <option value="">Please select...</option>
                    <option value="venue">Venue Owner / Manager</option>
                    <option value="user">App User / Customer</option>
                    <option value="carer">Carer / Support Worker</option>
                    <option value="assistant">Personal / Executive Assistant</option>
                    <option value="partner">Potential Partner</option>
                    <option value="media">Media / Press</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div className="v2-form-row">
                <div className="v2-form-group">
                  <label htmlFor="v2-venue-name" className="v2-form-label">Venue Name (if applicable)</label>
                  <input type="text" id="v2-venue-name" name="v2-venue-name" className="v2-form-input" placeholder="Your venue name" />
                </div>
              </div>
              <div className="v2-form-row">
                <div className="v2-form-group">
                  <label htmlFor="v2-message" className="v2-form-label">Message <span className="v2-required" aria-hidden="true">*</span></label>
                  <textarea id="v2-message" name="v2-message" className="v2-form-textarea" required aria-required="true" placeholder="Tell us how we can help you..."></textarea>
                </div>
              </div>
              <div className="v2-form-row">
                <ReCAPTCHA
                  ref={captchaRef}
                  sitekey="6LcjByUqAAAAAH-9xv35Jmu6slfjpN4JILI2nr-1"
                  onChange={(value) => setRecaptchaValue(value)}
                />
              </div>
              <button
                type="submit"
                className="v2-btn v2-btn-primary v2-btn-large v2-form-submit"
                disabled={isSubmitting || !recaptchaValue}
                aria-busy={isSubmitting}
                aria-label={isSubmitting ? 'Sending your message' : 'Send message'}
                style={{ opacity: (isSubmitting || !recaptchaValue) ? 0.6 : 1, cursor: (isSubmitting || !recaptchaValue) ? 'not-allowed' : 'pointer' }}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
              {formFeedback.message && (
                <div
                  role="alert"
                  aria-live="assertive"
                  aria-atomic="true"
                  className={`v2-form-feedback v2-form-feedback-${formFeedback.type}`}
                >
                  {formFeedback.message}
                </div>
              )}
              <p className="v2-form-note">We will get back to you within 1-2 business days.</p>
            </form>
          </div>
        </section>
      </main>

      {/* ===== 4.14 Footer ===== */}
      <footer className="v2-footer" role="contentinfo">
        <div className="v2-footer-inner">
          <div className="v2-footer-top">
            <div className="v2-footer-brand">
              <img src={logo} alt="Piatto" className="v2-footer-logo" />
              <p>Accessibility for Hospitality. Enabling all hospitality, for all people.</p>
            </div>
            <nav aria-label="Product links">
              <h3 className="v2-footer-heading">Product</h3>
              <ul className="v2-footer-links">
                <li><a href="#v2-get-app">Download App</a></li>
              </ul>
            </nav>
            <nav aria-label="Venue links">
              <h3 className="v2-footer-heading">Venues</h3>
              <ul className="v2-footer-links">
                <li><a href="/SignUp">Venue Sign-Up</a></li>
                <li><a href="/SignIn">Venue Log-In</a></li>
              </ul>
            </nav>
            <nav aria-label="Resources">
              <h3 className="v2-footer-heading">Resources</h3>
              <ul className="v2-footer-links">
                <li><a href="https://piatto.substack.com/" target="_blank" rel="noopener noreferrer">Blog</a></li>
                <li><a href="/accessibility-statement">Accessibility Statement</a></li>
              </ul>
            </nav>
          </div>
          <div className="v2-footer-social" aria-label="Social media links">
            <a href="http://youtube.com/@piattoapp" aria-label="YouTube" target="_blank" rel="noopener noreferrer">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"/>
                <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"/>
              </svg>
            </a>
            <a href="https://www.instagram.com/piattoit" aria-label="Instagram" target="_blank" rel="noopener noreferrer">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
              </svg>
            </a>
            <a href="https://www.facebook.com/piattoAUS" aria-label="Facebook" target="_blank" rel="noopener noreferrer">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
              </svg>
            </a>
            <a href="https://twitter.com/@PiattoNow" aria-label="X (Twitter)" target="_blank" rel="noopener noreferrer">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
            <a href="https://piatto.substack.com/" aria-label="Substack" target="_blank" rel="noopener noreferrer">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z"/>
              </svg>
            </a>
            <a href="https://www.linkedin.com/company/piattoaus/" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
                <rect x="2" y="9" width="4" height="12"/>
                <circle cx="4" cy="4" r="2"/>
              </svg>
            </a>
            <a href="https://discord.com/invite/6dcdt2d24x" aria-label="Discord" target="_blank" rel="noopener noreferrer">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.373-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
              </svg>
            </a>
          </div>
          <div className="v2-footer-bottom">
            <p className="v2-footer-copyright">&copy; {new Date().getFullYear()} Piatto. All rights reserved.</p>
            <FooterPatentList className="v2-footer-copyright" />
            <nav className="v2-footer-legal" aria-label="Legal links">
              <div className="v2-footer-legal-row">
                <a href="/Piatto_Privacy_Policy.html" target="_blank" rel="noopener noreferrer">Privacy Policy<span className="v2-sr-only"> (opens in new tab)</span></a>
                <a href="/Piatto_Complaint_Policy.pdf" target="_blank" rel="noopener noreferrer">Complaints Policy<span className="v2-sr-only"> (opens in new tab)</span></a>
                <a href="/Piatto_Data_Breach_Policy.pdf" target="_blank" rel="noopener noreferrer">Data Breach Policy<span className="v2-sr-only"> (opens in new tab)</span></a>
              </div>
              <div className="v2-footer-legal-row">
                <a href="/Piatto_Terms_and_Conditions_for_Venues.pdf" target="_blank" rel="noopener noreferrer">Terms and Conditions for Venues<span className="v2-sr-only"> (opens in new tab)</span></a>
                <a href="/Piatto_Terms_and_Conditions_for_Users.pdf" target="_blank" rel="noopener noreferrer">Terms and Conditions for Users<span className="v2-sr-only"> (opens in new tab)</span></a>
              </div>
            </nav>
          </div>
        </div>
      </footer>

      <style jsx>{`
        /* =========================================================
           LandingV2 — all classes prefixed v2- to avoid collision
           ========================================================= */

        /* Import Google Fonts */
        @import url('https://fonts.googleapis.com/css2?family=Atkinson+Hyperlegible+Next:ital,wght@0,400;0,700;1,400;1,700&family=Lexend:wght@300;400;500;600;700&display=swap');

        /* CSS Variables (scoped via specificity, not :root to avoid polluting v1) */
        .v2-hero,
        .v2-header,
        .v2-how-it-works,
        .v2-section,
        .v2-impact-band,
        .v2-callout-band,
        .v2-get-app-section,
        .v2-contact-section,
        .v2-footer {
          --font-size-base: 20px;
          --color-bg-dark: #0a0a0a;
          --color-bg-dark-alt: #1a1a1a;
          --color-bg-light: #f5f5f5;
          --color-bg-cream: #faf8f5;
          --color-surface: #ffffff;
          --color-text-dark: #1a1a1a;
          --color-text-light: #ffffff;
          --color-text-muted: #4a4a4a;
          --color-text-muted-light: rgba(255,255,255,0.85);
          --color-primary: #c41e3a;
          --color-primary-hover: #a01830;
          --color-primary-light: #fce8eb;
          --color-accent: #7cb342;
          --color-accent-hover: #689f38;
          --color-accent-light: #e8f5e9;
          --color-border: #e0e0e0;
          --color-border-dark: rgba(255,255,255,0.1);
          --shadow-md: 0 4px 20px rgba(0,0,0,0.1);
          --shadow-lg: 0 12px 40px rgba(0,0,0,0.2);
          --shadow-glow: 0 0 40px rgba(196, 30, 58, 0.3);
          --radius-sm: 12px;
          --radius-md: 20px;
          --radius-lg: 28px;
          --radius-full: 9999px;
          --transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          --transition-slow: 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          --font-primary: 'Atkinson Hyperlegible Next', system-ui, -apple-system, sans-serif;
          --font-secondary: 'Lexend', system-ui, -apple-system, sans-serif;
          --glass-bg: rgba(255, 255, 255, 0.08);
          --glass-border: rgba(255, 255, 255, 0.12);
        }

        /* ---- Scroll Progress ---- */
        .v2-scroll-progress {
          position: fixed;
          top: 0;
          left: 0;
          height: 3px;
          background: linear-gradient(90deg, var(--color-primary), var(--color-accent));
          width: 0%;
          z-index: 1000;
          transition: width 0.1s linear;
        }

        /* ---- Skip Link ---- */
        .v2-skip-link {
          position: absolute;
          top: -100%;
          left: 1rem;
          background: var(--color-primary);
          color: white;
          padding: 0.75rem 1.5rem;
          border-radius: 12px;
          text-decoration: none;
          font-weight: 600;
          z-index: 1001;
          font-family: 'Atkinson Hyperlegible Next', system-ui, -apple-system, sans-serif;
          transition: top 0.3s ease;
        }
        .v2-skip-link:focus {
          top: 1rem;
          outline: 3px solid #7cb342;
        }

        /* ---- Visually Hidden ---- */
        .v2-visually-hidden {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }
        .v2-sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0,0,0,0);
          white-space: nowrap;
          border: 0;
        }

        /* ---- Focus States ---- */
        .v2-header *:focus-visible,
        #v2-main-content *:focus-visible,
        .v2-footer *:focus-visible,
        .v2-a11y-toolbar *:focus-visible {
          outline: 3px solid #c41e3a;
          outline-offset: 3px;
        }

        /* ---- Accessibility Toolbar ---- */
        .v2-a11y-toolbar {
          position: fixed;
          bottom: 1.5rem;
          right: 1.5rem;
          z-index: 99;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .v2-a11y-btn {
          width: 50px;
          height: 50px;
          border-radius: 9999px;
          border: 2px solid #e0e0e0;
          background: #ffffff;
          color: #1a1a1a;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 20px rgba(0,0,0,0.1);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          backdrop-filter: blur(10px);
          font-family: 'Atkinson Hyperlegible Next', system-ui, -apple-system, sans-serif;
        }
        .v2-a11y-btn:hover, .v2-a11y-btn:focus {
          background: #c41e3a;
          color: white;
          border-color: #c41e3a;
          transform: scale(1.1);
          box-shadow: 0 0 40px rgba(196, 30, 58, 0.3);
        }
        .v2-a11y-btn svg { width: 24px; height: 24px; }
        .v2-a11y-btn[aria-pressed="true"] { background: #7cb342; color: white; border-color: #7cb342; }
        .v2-a11y-btn-hidden { opacity: 0; transform: scale(0); pointer-events: none; }
        .v2-a11y-btn-visible { opacity: 1; transform: scale(1); pointer-events: auto; }
        .v2-a11y-btn-tooltip {
          position: absolute;
          right: 60px;
          background: #0a0a0a;
          color: #ffffff;
          padding: 0.5rem 0.75rem;
          border-radius: 12px;
          font-size: 0.85rem;
          white-space: nowrap;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.3s ease;
        }
        .v2-a11y-btn:hover .v2-a11y-btn-tooltip,
        .v2-a11y-btn:focus .v2-a11y-btn-tooltip { opacity: 1; }

        /* ---- Header ---- */
        .v2-header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 100;
          background: rgba(10, 10, 10, 0.7);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.12);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          font-family: var(--font-primary);
        }
        #v2-venues,
        #v2-carers,
        #v2-get-app,
        #v2-contact {
          scroll-margin-top: 5rem;
        }
        .v2-header.v2-scrolled {
          background: rgba(10, 10, 10, 0.95);
          box-shadow: 0 4px 30px rgba(0, 0, 0, 0.3);
        }
        .v2-header-inner {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0.75rem 1.5rem;
          display: flex;
          align-items: center;
          gap: 1.5rem;
        }
        .v2-logo { display: flex; align-items: center; text-decoration: none; }
        .v2-logo-image {
          height: 55px;
          width: auto;
          object-fit: contain;
          transition: transform 0.3s ease;
        }
        .v2-logo:hover .v2-logo-image { transform: scale(1.05); }

        .v2-nav-center {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 2.5rem;
          margin-left: auto;
          transition: margin 0.8s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .v2-scrolled .v2-nav-center {
          margin-left: auto;
          margin-right: auto;
        }
        .v2-nav-center-links {
          display: flex;
          align-items: center;
          gap: 2.5rem;
        }
        .v2-nav-link {
          color: #ffffff;
          text-decoration: none;
          font-size: 0.95rem;
          font-weight: 500;
          transition: all 0.3s ease;
          white-space: nowrap;
          position: relative;
        }
        .v2-nav-link::after {
          content: '';
          position: absolute;
          bottom: -4px;
          left: 0;
          width: 0;
          height: 2px;
          background: linear-gradient(90deg, #c41e3a, #7cb342);
          transition: width 0.3s ease;
        }
        .v2-nav-link:hover::after, .v2-nav-link:focus::after { width: 100%; }
        .v2-nav-link:hover, .v2-nav-link:focus { color: rgba(255,255,255,0.85); }

        .v2-header-cta {
          opacity: 0;
          transform: translateY(10px);
          pointer-events: none;
          flex-shrink: 0;
          transition: opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1),
                      transform 0.8s cubic-bezier(0.4, 0, 0.2, 1),
                      box-shadow 0.3s ease;
          padding: 0.5rem 1.1rem;
          font-size: 0.85rem;
          white-space: nowrap;
        }
        .v2-header-cta.v2-header-cta-visible {
          opacity: 1;
          transform: translateY(0);
          pointer-events: auto;
        }

        .v2-header-cta-mobile {
          opacity: 0;
          transform: translateY(10px);
          pointer-events: none;
          transition: opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1),
                      transform 0.8s cubic-bezier(0.4, 0, 0.2, 1),
                      box-shadow 0.3s ease;
          padding: 0.4rem 0.9rem;
          font-size: 0.8rem;
          white-space: nowrap;
        }
        .v2-header-cta-mobile.v2-header-cta-visible {
          opacity: 1;
          transform: translateY(0);
          pointer-events: auto;
        }

        @media (min-width: 769px) {
          .v2-header-cta-mobile { display: none !important; }
        }
        @media (max-width: 768px) {
          .v2-nav-center { display: none; }
          .v2-header-cta { display: none !important; }
        }

        .v2-mobile-menu-btn {
          display: none;
          flex-direction: column;
          gap: 5px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0.5rem;
        }
        @media (max-width: 768px) { .v2-mobile-menu-btn { display: flex; } }
        .v2-mobile-menu-btn span {
          width: 24px;
          height: 2px;
          background: #ffffff;
          border-radius: 2px;
          transition: all 0.3s ease;
        }
        .v2-mobile-menu-btn[aria-expanded="true"] span:nth-child(1) {
          transform: rotate(45deg) translate(5px, 5px);
        }
        .v2-mobile-menu-btn[aria-expanded="true"] span:nth-child(2) { opacity: 0; }
        .v2-mobile-menu-btn[aria-expanded="true"] span:nth-child(3) {
          transform: rotate(-45deg) translate(5px, -5px);
        }

        .v2-mobile-nav {
          position: fixed;
          top: 60px;
          right: -300px;
          width: 250px;
          background: rgba(10, 10, 10, 0.98);
          backdrop-filter: blur(20px);
          padding: 1.5rem 1rem;
          z-index: 99;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          box-shadow: -4px 0 20px rgba(0, 0, 0, 0.5);
          transition: right 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border-left: 1px solid rgba(255, 255, 255, 0.12);
        }
        .v2-mobile-nav.v2-open { right: 0; }
        .v2-mobile-nav a {
          display: block;
          padding: 1rem;
          color: #ffffff;
          text-decoration: none;
          font-size: 1rem;
          font-weight: 500;
          font-family: 'Atkinson Hyperlegible Next', system-ui, -apple-system, sans-serif;
          border-radius: 12px;
          transition: all 0.3s ease;
          text-align: left;
        }
        .v2-mobile-nav a:hover {
          background: rgba(255, 255, 255, 0.08);
          transform: translateX(-5px);
        }

        /* ---- Buttons ---- */
        .v2-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          font-family: 'Atkinson Hyperlegible Next', system-ui, -apple-system, sans-serif;
          font-size: 0.95rem;
          font-weight: 600;
          text-decoration: none;
          border-radius: 12px;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }
        .v2-btn-primary {
          background: linear-gradient(135deg, #7cb342, #5a9216);
          color: #ffffff;
          box-shadow: 0 4px 15px rgba(124, 179, 66, 0.3);
        }
        .v2-btn-primary:hover, .v2-btn-primary:focus {
          transform: translateY(-3px);
          box-shadow: 0 8px 25px rgba(124, 179, 66, 0.4);
        }
        .v2-btn-primary::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
          transition: left 0.5s ease;
        }
        .v2-btn-primary:hover::before { left: 100%; }
        .v2-btn-large { padding: 1rem 2rem; font-size: 1.1rem; }

        .v2-free-badge {
          display: inline-block;
          background: rgba(255,255,255,0.2);
          color: #ffffff;
          font-size: 0.65rem;
          font-weight: 700;
          padding: 0.2rem 0.5rem;
          border-radius: 9999px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-left: 0.4rem;
        }
        .v2-free-badge-alt {
          display: inline-block;
          background: #e8f5e9;
          color: #689f38;
          font-size: 0.65rem;
          font-weight: 700;
          padding: 0.2rem 0.5rem;
          border-radius: 9999px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-left: 0.5rem;
        }

        /* ---- Hero Section ---- */
        .v2-hero {
          background: #0a0a0a;
          color: #ffffff;
          padding: 2rem 1.5rem 4rem;
          position: relative;
          overflow: hidden;
          min-height: 100vh;
          display: flex;
          align-items: center;
          font-family: 'Atkinson Hyperlegible Next', system-ui, -apple-system, sans-serif;
        }
        @media (max-width: 991px) {
          .v2-hero {
            align-items: flex-start;
            padding-top: 5rem;
          }
        }
        .v2-hero-gradient {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background:
            radial-gradient(ellipse 80% 50% at 20% 40%, rgba(196, 30, 58, 0.2) 0%, transparent 50%),
            radial-gradient(ellipse 60% 40% at 80% 60%, rgba(124, 179, 66, 0.1) 0%, transparent 50%);
          pointer-events: none;
        }
        .v2-hero-inner {
          max-width: 1200px;
          margin: 0 auto;
          position: relative;
          z-index: 2;
          width: 100%;
        }
        .v2-hero-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 3rem;
          align-items: center;
        }
        @media (min-width: 992px) {
          .v2-hero-grid {
            grid-template-columns: 1.2fr 1fr;
            gap: 4rem;
          }
        }
        .v2-hero-title {
          font-size: clamp(2.5rem, 6vw, 3.75rem);
          font-weight: 700;
          line-height: 1.1;
          margin-bottom: 1.5rem;
          background: linear-gradient(135deg, #ffffff 0%, #ffffff 50%, #c41e3a 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .v2-hero-subtitle {
          font-size: clamp(1rem, 2vw, 1.2rem);
          color: rgba(255,255,255,0.85);
          font-weight: 400;
          line-height: 1.8;
          margin-bottom: 2rem;
        }
        .v2-hero-cta { margin-bottom: 1.5rem; }
        .v2-hero-app-note {
          font-size: 0.95rem;
          color: rgba(255,255,255,0.7);
        }
        .v2-hero-link {
          color: #7cb342;
          text-decoration: underline;
          text-underline-offset: 3px;
          transition: color 0.3s ease;
        }
        .v2-hero-link:hover { color: #9ccc65; }
        .v2-hero-visual {
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .v2-hero-image {
          width: 100%;
          max-width: 480px;
          height: auto;
          border-radius: 28px;
          filter: drop-shadow(0 20px 60px rgba(196, 30, 58, 0.2));
        }

        /* ---- Scroll Reveal ---- */
        :global(.v2-reveal) {
          opacity: 0;
          transform: translateY(40px);
          transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
        }
        :global(.v2-reveal.v2-revealed) {
          opacity: 1;
          transform: translateY(0);
        }
        :global(.v2-reveal-left) {
          opacity: 0;
          transform: translateX(-60px);
          transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
        }
        :global(.v2-reveal-left.v2-revealed) {
          opacity: 1;
          transform: translateX(0);
        }
        :global(.v2-reveal-right) {
          opacity: 0;
          transform: translateX(60px);
          transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
        }
        :global(.v2-reveal-right.v2-revealed) {
          opacity: 1;
          transform: translateX(0);
        }
        :global(.v2-reveal-scale) {
          opacity: 0;
          transform: scale(0.9);
          transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
        }
        :global(.v2-reveal-scale.v2-revealed) {
          opacity: 1;
          transform: scale(1);
        }

        /* ---- Reading Highlight ---- */
        :global(.v2-reading-highlight) {
          background: rgba(124, 179, 66, 0.3) !important;
          outline: 2px solid #7cb342;
        }

        /* ---- Impact Band ---- */
        .v2-impact-band {
          background: linear-gradient(135deg, #8b1528, #6b1020);
          color: #ffffff;
          text-align: center;
          padding: 3rem 1.5rem;
          position: relative;
          font-family: 'Atkinson Hyperlegible Next', system-ui, -apple-system, sans-serif;
        }
        .v2-impact-text {
          font-size: clamp(1.15rem, 2.5vw, 1.5rem);
          font-weight: 600;
          max-width: 700px;
          margin: 0 auto;
          line-height: 1.6;
        }

        /* ---- Section Layout ---- */
        .v2-section {
          padding: 6rem 1.5rem;
          font-family: 'Atkinson Hyperlegible Next', system-ui, -apple-system, sans-serif;
        }
        .v2-section-inner { max-width: 1100px; margin: 0 auto; }
        .v2-section-light { background: #f5f5f5; }
        .v2-section-white { background: #ffffff; }
        .v2-section-cream { background: #faf8f5; }
        .v2-section-header { text-align: center; max-width: 850px; margin: 0 auto 4rem; }
        .v2-section-title {
          font-size: clamp(2rem, 4vw, 3rem);
          font-weight: 700;
          line-height: 1.2;
          margin-bottom: 1rem;
          color: #1a1a1a;
        }
        .v2-section-description {
          font-size: 1.15rem;
          color: #4a4a4a;
          line-height: 1.8;
        }

        /* ---- How It Works ---- */
        .v2-how-it-works {
          background: #ffffff;
          padding: 6rem 1.5rem;
          font-family: 'Atkinson Hyperlegible Next', system-ui, -apple-system, sans-serif;
        }
        .v2-how-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
        }
        @media (min-width: 768px) { .v2-how-grid { grid-template-columns: 1fr 1fr; gap: 3rem; } }

        .v2-how-card {
          background: #f5f5f5;
          border-radius: 28px;
          padding: 2.5rem;
          border: 1px solid #e0e0e0;
          transition: all 0.3s ease;
        }
        .v2-how-card:hover {
          box-shadow: 0 12px 40px rgba(0,0,0,0.12);
        }
        .v2-how-card h3 {
          font-size: 1.35rem;
          font-weight: 700;
          margin-bottom: 1.75rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
          color: #1a1a1a;
        }
        .v2-how-steps { list-style: none; counter-reset: step; padding: 0; margin: 0; }
        .v2-how-steps li {
          counter-increment: step;
          display: flex;
          align-items: flex-start;
          gap: 1.25rem;
          margin-bottom: 1.5rem;
          font-size: 1.1rem;
          color: #4a4a4a;
        }
        .v2-how-steps li::before {
          content: counter(step);
          flex-shrink: 0;
          width: 36px;
          height: 36px;
          background: linear-gradient(135deg, #c41e3a, #ff6b6b);
          color: white;
          border-radius: 9999px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 0.95rem;
          box-shadow: 0 4px 15px rgba(196, 30, 58, 0.3);
        }
        .v2-how-steps li strong { color: #c41e3a; }

        /* ---- Venue Cards ---- */
        .v2-venue-cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
        }
        .v2-venue-card {
          background: #ffffff;
          border-radius: 28px;
          padding: 2.25rem;
          border: 1px solid #e0e0e0;
          transition: all 0.3s ease;
        }
        .v2-venue-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 12px 40px rgba(0,0,0,0.12);
          border-color: #c41e3a;
        }
        .v2-venue-card-icon {
          width: 64px;
          height: 64px;
          margin-bottom: 1.25rem;
        }
        .v2-venue-card-icon img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
        .v2-venue-card h3 {
          font-size: 1.15rem;
          font-weight: 700;
          margin-bottom: 0.75rem;
          color: #1a1a1a;
        }
        .v2-venue-card p {
          color: #4a4a4a;
          font-size: 1rem;
          line-height: 1.8;
        }
        .v2-venue-cta {
          text-align: center;
          margin-top: 3.5rem;
        }

        /* ---- Problem Section ---- */
        .v2-problem-intro {
          max-width: 800px;
          margin: 0 auto 3rem;
          text-align: center;
        }
        .v2-problem-intro p {
          font-size: 1.15rem;
          color: #4a4a4a;
          line-height: 1.8;
          margin-bottom: 1.25rem;
        }
        .v2-problem-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 3rem;
          align-items: start;
        }
        @media (min-width: 768px) {
          .v2-problem-grid {
            grid-template-columns: 1fr 1fr;
            gap: 4rem;
          }
        }
        .v2-problem-point {
          margin-bottom: 2rem;
          padding-left: 1.5rem;
          border-left: 3px solid #c41e3a;
        }
        .v2-problem-point h3 {
          font-size: 1.15rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          color: #1a1a1a;
        }
        .v2-problem-point p {
          font-size: 1.05rem;
          color: #4a4a4a;
          line-height: 1.75;
        }
        .v2-problem-tagline {
          margin-top: 1.5rem;
          padding: 1.5rem;
          background: linear-gradient(135deg, #fce8eb, #fff0f3);
          border-radius: 20px;
        }
        .v2-problem-tagline p {
          font-size: 1.25rem;
          font-weight: 700;
          color: #c41e3a;
          margin: 0;
          text-align: center;
        }
        .v2-problem-visuals {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }

        /* ---- Feature Blocks ---- */
        .v2-feature-block {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
          align-items: center;
          margin-bottom: 5rem;
        }
        .v2-feature-block:last-child { margin-bottom: 0; }
        @media (min-width: 768px) {
          .v2-feature-block { grid-template-columns: 1fr 1fr; gap: 4rem; }
          .v2-feature-block.v2-reverse .v2-feature-content { order: 2; }
          .v2-feature-block.v2-reverse .v2-feature-visual { order: 1; }
        }
        .v2-feature-content h2 {
          font-size: clamp(1.75rem, 3.5vw, 2.25rem);
          font-weight: 700;
          margin-bottom: 1.25rem;
          color: #1a1a1a;
          line-height: 1.3;
        }
        .v2-feature-content h3 {
          font-size: 1.4rem;
          font-weight: 700;
          margin-bottom: 1rem;
          color: #1a1a1a;
          line-height: 1.35;
        }
        .v2-feature-content p {
          font-size: 1.1rem;
          color: #4a4a4a;
          margin-bottom: 1.25rem;
          line-height: 1.85;
        }
        .v2-feature-visual { display: flex; justify-content: center; }
        .v2-feature-image {
          width: 100%;
          max-width: 450px;
          height: auto;
          border-radius: 28px;
          object-fit: cover;
          transition: all 0.3s ease;
          box-shadow: 0 4px 20px rgba(0,0,0,0.1);
        }
        .v2-feature-image:hover {
          box-shadow: 0 12px 40px rgba(0,0,0,0.15);
          filter: brightness(1.03);
        }

        /* ---- Pink Callout Band ---- */
        .v2-callout-band {
          background: linear-gradient(135deg, #fce8eb, #f8d7da);
          text-align: center;
          padding: 3.5rem 1.5rem;
          font-family: 'Atkinson Hyperlegible Next', system-ui, -apple-system, sans-serif;
        }
        .v2-callout-text {
          font-size: clamp(1.1rem, 2.5vw, 1.4rem);
          font-weight: 600;
          color: #8b1528;
          max-width: 700px;
          margin: 0 auto;
          line-height: 1.6;
        }

        /* ---- Carers Section ---- */
        .v2-carers-section {
          background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 50%, #a5d6a7 100%);
          position: relative;
          overflow: hidden;
        }
        .v2-carers-section::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -20%;
          width: 60%;
          height: 200%;
          background: radial-gradient(ellipse, rgba(255,255,255,0.4) 0%, transparent 70%);
          pointer-events: none;
        }
        .v2-carers-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
          align-items: center;
          position: relative;
          z-index: 1;
        }
        @media (min-width: 768px) { .v2-carers-grid { grid-template-columns: 1.2fr 1fr; gap: 4rem; } }
        .v2-carers-content h2 {
          font-size: clamp(1.75rem, 3.5vw, 2.25rem);
          font-weight: 700;
          margin-bottom: 1.25rem;
          color: #1a1a1a;
        }
        .v2-carers-content > p {
          font-size: 1.15rem;
          color: #4a4a4a;
          margin-bottom: 1.75rem;
          line-height: 1.8;
        }
        .v2-carers-list { list-style: none; margin-bottom: 2rem; padding: 0; }
        .v2-carers-list li {
          padding: 0.6rem 0;
          padding-left: 2.25rem;
          position: relative;
          font-size: 1.1rem;
          color: #1a1a1a;
        }
        .v2-carers-list li::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0.85rem;
          width: 10px;
          height: 10px;
          background: #689f38;
          border-radius: 50%;
        }
        .v2-carers-quote {
          background: #ffffff;
          border-radius: 20px;
          padding: 1.75rem;
          border-left: 4px solid #7cb342;
          box-shadow: 0 4px 20px rgba(0,0,0,0.1);
        }
        .v2-carers-quote p {
          color: #4a4a4a;
          margin: 0;
          font-size: 1.05rem;
          line-height: 1.75;
        }
        .v2-carers-visual { display: flex; justify-content: center; }

        /* ---- Accessibility Grid ---- */
        .v2-a11y-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2rem;
        }
        .v2-a11y-card {
          background: #ffffff;
          border-radius: 28px;
          padding: 2.25rem;
          border: 1px solid #e0e0e0;
          transition: all 0.3s ease;
        }
        .v2-a11y-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 12px 40px rgba(0,0,0,0.12);
          border-color: #c41e3a;
        }
        .v2-a11y-card-icon {
          width: 64px;
          height: 64px;
          background: linear-gradient(135deg, #c41e3a, #ff6b6b);
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1.5rem;
          color: white;
          box-shadow: 0 8px 20px rgba(196, 30, 58, 0.3);
        }
        .v2-a11y-card-icon svg { width: 30px; height: 30px; }
        .v2-a11y-card h3 {
          font-size: 1.15rem;
          font-weight: 600;
          margin-bottom: 0.75rem;
          color: #1a1a1a;
        }
        .v2-a11y-card p {
          color: #4a4a4a;
          font-size: 1rem;
          line-height: 1.8;
        }

        /* ---- Get the App Section ---- */
        .v2-get-app-section {
          background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%);
          color: #ffffff;
          text-align: center;
          padding: 6rem 1.5rem;
          position: relative;
          overflow: hidden;
          font-family: 'Atkinson Hyperlegible Next', system-ui, -apple-system, sans-serif;
        }
        .v2-get-app-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background:
            radial-gradient(ellipse 50% 80% at 20% 50%, rgba(196, 30, 58, 0.15) 0%, transparent 50%),
            radial-gradient(ellipse 50% 80% at 80% 50%, rgba(124, 179, 66, 0.1) 0%, transparent 50%);
          pointer-events: none;
        }
        .v2-get-app-inner {
          max-width: 700px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }
        .v2-get-app-title {
          font-size: clamp(2rem, 4vw, 2.75rem);
          font-weight: 700;
          margin-bottom: 1rem;
        }
        .v2-get-app-title-link {
          color: inherit;
          text-decoration: none;
          font: inherit;
        }
        .v2-get-app-title-link:hover,
        .v2-get-app-title-link:focus-visible {
          text-decoration: underline;
        }
        .v2-get-app-text {
          font-size: 1.2rem;
          opacity: 0.9;
          margin-bottom: 2.5rem;
        }
        .v2-app-badges {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 1.25rem;
        }
        .v2-app-badge-link {
          display: inline-block;
          text-decoration: none;
          transition: all 0.3s ease;
          border-radius: 8px;
        }
        .v2-app-badge-img {
          height: 58px;
          width: auto;
          display: block;
          border-radius: 8px;
          transition: all 0.3s ease;
        }
        .v2-app-badge-link:hover .v2-app-badge-img {
          transform: translateY(-3px);
          box-shadow: 0 10px 30px rgba(0,0,0,0.4);
        }
        .v2-app-badge-link:focus {
          outline: 2px solid #7cb342;
          outline-offset: 3px;
        }

        /* ---- Contact Section ---- */
        .v2-contact-section {
          background: #0a0a0a;
          color: #ffffff;
          padding: 6rem 1.5rem;
          position: relative;
          font-family: 'Atkinson Hyperlegible Next', system-ui, -apple-system, sans-serif;
        }
        .v2-contact-inner { max-width: 700px; margin: 0 auto; }
        .v2-contact-header { text-align: center; margin-bottom: 3rem; }
        .v2-contact-title {
          font-size: clamp(2rem, 4vw, 2.5rem);
          font-weight: 700;
          margin-bottom: 1rem;
        }
        .v2-contact-subtitle {
          font-size: 1.15rem;
          color: rgba(255,255,255,0.85);
        }
        .v2-contact-form {
          background: rgba(255, 255, 255, 0.08);
          border-radius: 28px;
          padding: 3rem;
          border: 1px solid rgba(255, 255, 255, 0.12);
          backdrop-filter: blur(20px);
          box-shadow: 0 8px 32px rgba(0,0,0,0.3);
        }
        .v2-form-row {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
          margin-bottom: 1.5rem;
        }
        @media (min-width: 600px) { .v2-form-row.v2-two-col { grid-template-columns: 1fr 1fr; } }
        .v2-form-group { display: flex; flex-direction: column; }
        .v2-form-label {
          font-size: 0.95rem;
          font-weight: 600;
          margin-bottom: 0.6rem;
          color: #ffffff;
        }
        .v2-required { color: #c41e3a; }
        .v2-form-input, .v2-form-textarea, .v2-form-select {
          width: 100%;
          padding: 1rem 1.25rem;
          font-family: 'Atkinson Hyperlegible Next', system-ui, -apple-system, sans-serif;
          font-size: 1rem;
          color: #ffffff;
          background: rgba(255,255,255,0.08);
          border: 2px solid rgba(255, 255, 255, 0.12);
          border-radius: 12px;
          transition: all 0.3s ease;
        }
        .v2-form-input::placeholder, .v2-form-textarea::placeholder { color: rgba(255,255,255,0.5); }
        .v2-form-input:focus, .v2-form-textarea:focus, .v2-form-select:focus {
          outline: none;
          border-color: #7cb342;
          box-shadow: 0 0 0 3px rgba(124, 179, 66, 0.2);
          background: rgba(255,255,255,0.12);
        }
        .v2-form-textarea { min-height: 140px; resize: vertical; }
        .v2-form-select {
          cursor: pointer;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 1rem center;
          background-size: 1.25rem;
          padding-right: 3rem;
        }
        .v2-form-select option { background: #0a0a0a; color: #ffffff; }
        .v2-form-submit { width: 100%; margin-top: 1.25rem; }
        .v2-form-note {
          text-align: center;
          margin-top: 1.5rem;
          font-size: 0.95rem;
          color: rgba(255,255,255,0.85);
        }
        .v2-form-feedback {
          text-align: center;
          margin-top: 1rem;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          font-weight: 500;
        }
        .v2-form-feedback-success {
          background: rgba(124, 179, 66, 0.15);
          color: #7cb342;
          border: 1px solid rgba(124, 179, 66, 0.3);
        }
        .v2-form-feedback-error {
          background: rgba(196, 30, 58, 0.15);
          color: #ef5350;
          border: 1px solid rgba(196, 30, 58, 0.3);
        }
        .v2-phone-hint {
          font-size: 0.85rem;
          color: rgba(255,255,255,0.85);
          margin-top: 0.25rem;
        }

        /* ---- Footer ---- */
        .v2-footer {
          background: #0a0a0a;
          color: #ffffff;
          padding: 5rem 1.5rem 1.5rem;
          border-top: 1px solid rgba(255,255,255,0.1);
          font-family: 'Atkinson Hyperlegible Next', system-ui, -apple-system, sans-serif;
        }
        .v2-footer-inner { max-width: 1200px; margin: 0 auto; }
        .v2-footer-top {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
          margin-bottom: 2rem;
        }
        @media (min-width: 768px) { .v2-footer-top { grid-template-columns: 2fr 1fr 1fr 1fr; } }
        .v2-footer-logo {
          height: 50px;
          width: auto;
          display: block;
        }
        .v2-footer-brand p {
          color: rgba(255,255,255,0.85);
          font-size: 1rem;
          max-width: 300px;
          margin-top: 1.25rem;
          line-height: 1.75;
        }
        .v2-footer-heading {
          font-weight: 600;
          margin-bottom: 1.25rem;
          margin-top: 0.5rem;
          font-size: 1.05rem;
          color: #ffffff;
        }
        .v2-footer-links { list-style: none; padding: 0; margin: 0; }
        .v2-footer-links li { margin-bottom: 0.75rem; }
        .v2-footer-links a {
          color: rgba(255,255,255,0.85);
          text-decoration: none;
          font-size: 0.95rem;
          transition: all 0.3s ease;
          position: relative;
        }
        .v2-footer-links a:hover { color: #ffffff; padding-left: 8px; }
        .v2-footer-social {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 1rem;
          padding: 2.5rem 0;
          border-top: 1px solid rgba(255,255,255,0.1);
          border-bottom: 1px solid rgba(255,255,255,0.1);
          margin-bottom: 2.5rem;
        }
        .v2-footer-social a {
          width: 50px;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 12px;
          color: #ffffff;
          transition: all 0.3s ease;
        }
        .v2-footer-social a:hover {
          background: #c41e3a;
          border-color: #c41e3a;
          transform: translateY(-4px) scale(1.05);
          box-shadow: 0 8px 20px rgba(196, 30, 58, 0.3);
        }
        .v2-footer-social svg { width: 22px; height: 22px; }
        .v2-footer-bottom { text-align: center; }
        .v2-footer-copyright {
          color: rgba(255,255,255,0.85);
          font-size: 0.95rem;
          margin-bottom: 1.25rem;
        }
        .v2-footer-legal {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.6rem;
        }
        .v2-footer-legal-row {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 0.5rem 1.75rem;
        }
        .v2-footer-legal a {
          color: rgba(255,255,255,0.85);
          text-decoration: underline;
          font-size: 0.9rem;
          transition: color 0.3s ease;
        }
        .v2-footer-legal a:hover { color: #ffffff; }
        .v2-footer-legal a:focus-visible {
          outline: 2px solid #fff;
          outline-offset: 3px;
          border-radius: 2px;
        }

        /* ---- Reduced Motion ---- */
        @media (prefers-reduced-motion: reduce) {
          .v2-hero-gradient { animation: none; }
          :global(.v2-reveal),
          :global(.v2-reveal-left),
          :global(.v2-reveal-right),
          :global(.v2-reveal-scale) {
            opacity: 1;
            transform: none;
          }
          .v2-venue-card,
          .v2-a11y-card,
          .v2-how-card {
            transform: none !important;
          }
          .v2-btn-primary::before { display: none; }
          .v2-header-cta,
          .v2-header-cta-mobile {
            transition: none;
            transform: none;
          }
        }

        /* ---- Print ---- */
        @media print {
          .v2-header,
          .v2-get-app-section,
          .v2-mobile-menu-btn,
          .v2-contact-section,
          .v2-a11y-toolbar,
          .v2-scroll-progress { display: none; }
          .v2-hero { padding: 2rem 0; min-height: auto; }
        }
      `}</style>
    </>
  );
};

export default LandingV2;
